import { useEffect, useState } from 'react';
import { Input } from '@vwt/ui';

/** A real calendar day: browsers report partial years (0002-…) while someone is still typing. */
export const isPlausibleDate = (value: string) => /^(19|20|21)\d{2}-\d{2}-\d{2}$/.test(value);

/**
 * Native date input that commits once, on blur or Enter, never on each keystroke: typing a year
 * would otherwise save 0002-…, 0020-… and 0202-… first.
 */
export function DateInput({ value, onCommit, ...props }: { value: string | null; onCommit: (value: string | null) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);
  const commit = (next: string) => {
    if (next === (value ?? '')) return;
    if (next === '') onCommit(null);
    else if (isPlausibleDate(next)) onCommit(next);
    else setDraft(value ?? '');
  };
  return (
    <Input
      {...props}
      type="date"
      value={draft}
      // Picking from the calendar leaves focus here, so leaving the field (or Enter) commits it too.
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit(e.currentTarget.value);
        }
      }}
    />
  );
}
